import { ReactNode } from 'react';
import { ResearchRole } from './global-enums';
import { ArticleState } from './global-enums';

export interface IdAndName {
    _id: string;
    name: string;
}

export interface IdAndTitle {
    _id: string;
    title: string;
}

export interface UserGuideScope {
    _id: string;
    version: number;
    scope: string;
    name: string;
    weight?: number;
    createdAt?: string;
}

export interface Publisher extends IdAndName {
    type: ChannelOwner;
}

export type DeskCategory = IdAndName;

export interface CategoryItem {
    _id: string;
    name: string;
    description: string;
    parent: string | undefined;
    articleCount: number;
    version: number;
    views?: number;
    isFeatured: boolean;
    smallImage?: string;
    largeImage?: string;
    subscribersCount?: number;
    promotionalContentId: string | undefined;
    promotionalContent: PromotionalContent;
    isLocked: boolean;
    isPremium: boolean;
    rootCategory?: CategoryItem;
}

export interface CategoryItemNode extends CategoryItem {
    level: number;
    children: CategoryItemNode[];
    parentNode?: CategoryItemNode;
}

export interface CategoryItemByParent {
    parentId: string;
    children: CategoryItem[];
}

export interface CategoriesTreeRowItem {
    _id: string;
    name: string;
    categoriesHierarchy: string[];
    description: string;
    views: number | undefined;
    articleCount: number;
    subscribersCount: number;
    subscribers?: User[];
}

export interface TagItem {
    _id: string;
    name: string;
    description: string;
    articleCount: number;
    version: number;
    views?: number;
}

export interface Desk {
    _id: string;
    name: string;
    contributors: User[];
    description: string;
    version: number;
    views?: number;
    categories?: DeskCategory[];
    email?: string;
    channels?: Channel[];
}

export interface Contributor {
    _id: string;
    name: string;
    position: string;
    desks?: Desk[];
    categories?: DeskCategory[];
    channels?: Channel[];
}

export interface Subscriptions {
    categories: CategorySubscription[];
}

export interface ShortDashboardUser {
    neonUserId: string;
    auth0email: string;
    firstName: string;
    lastName: string;
    company: string;
}

export interface ShortUser {
    _id: string;
    name: string;
    email: string;
    company: string;
}

export interface User extends ShortUser {
    firstLogin: string;
    lastLogin: string;
    scopes: string[];
    role: ResearchRole;
    phone: string;
    position: string;
    version: number;
    views?: number;
    nonUniqueViews?: number;
    articleStats?: UserArticleStats;
    trialMinLeft?: number;
    displayAvatar: boolean;
    tableauAccess: boolean;
    subscriptions: Subscriptions;
    channelSubscriptions: ChannelSubscription[];
}

export interface IsUserProfileComplete {
    isProfileComplete: boolean;
}

export interface UserArticleStats {
    articleId: string;
    views: number;
    lastViewed: string;
}

export interface AlertMessage {
    style: 'error' | 'warning' | 'success' | 'info' | 'grey';
    message: JSX.Element | string | undefined | null;
    id?: string;
}

export interface Article {
    _id: string;
    author: string;
    title: string;
    html: string;
    summary: string;
    publishedBy: Publisher;
    contributors: Partial<User>[];
    categories: IdAndName[];
    tags: IdAndName[];
    state: ArticleState;
    created?: number;
    version: number;
    lastEdited?: Date;
    publishDate?: Date;
    views?: number;
    nonUniqueViews?: number;
    viewers?: User[];
    wasPublishedPreviously?: boolean;
    randomisedPromotionalContent?: PromotionalContentSummary;
    promotionalContent?: [PromotionalContentSummary];
    isPremium: boolean;
    isPremiumActive: boolean;
    premiumExpiryOffset: string;
    premiumChannel: string;
    isUserGuideArticle: boolean;
    userGuideScopes: string[];
    canSeeFullVersion: boolean;
    deskEmail: string;
    isLocked: boolean;
    premiumChannelDetails?: Channel;
    showWatermark: boolean;
    displayInChartSection: boolean;
    isPinned: boolean;
    isTimeless: boolean;
}

export interface ArticleSummary {
    _id: string;
    title: string;
    summary: string;
    publishedBy: Publisher;
    parentCategories: IdAndName[];
    rootCategories: IdAndName[];
    categories: IdAndName[];
    publishDate: Date;
    lastEdited?: Date;
    isPremium: boolean;
    isPremiumActive: boolean;
    canSeeFullVersion: boolean;
    isLocked: boolean;
    premiumChannelDetails?: Channel;
    isPinned: boolean;
    isUserGuideArticle: boolean;
    userGuideScopes: string[];
}

export interface ArticleDisplay extends Article {
    publishedDate?: string;
    premiumExpiresDate?: string;
}

export interface Children {
    children: ReactNode;
}

export interface ArticleUpsertDto {
    _id?: string;
    author?: string;
    title: string;
    html: string;
    summary: string;
    publishedBy?: Omit<Publisher, 'name'> | null;
    contributors: string[];
    categories: string[];
    tags: string[];
    state: ArticleState;
    created?: Date;
    lastEdited?: Date;
    version?: number;
    publishDate?: Date;
    sendEmails?: boolean;
    templateId?: string;
    isPremium?: boolean;
    promotionalContentIds: string[];
    premiumExpiryOffset: string;
    premiumChannel?: string | null;
    showWatermark: boolean;
    displayInChartSection: boolean;
    isPinned: boolean;
    isTimeless: boolean;
    isUserGuideArticle: boolean;
    userGuideScopes: string[];
}

export interface ArticleInfo {
    articleState?: ArticleState;
    createDate?: Date;
    updateDate?: Date;
}

export interface ArticleViewStatAdd {
    type: string;
    source: string;
    session: string;
    articleId: string;
    tags: IdAndName[];
    categories: IdAndName[];
    desk?: IdAndName;
}

export interface PaginatedResult<T> {
    documents: T[];
    totalCount: number;
    pageInfo: {
        endCursor: string;
        hasNextPage: boolean;
    };
}

export interface RecommendedArticlesResult<T> {
    latestArticles: T[];
    recommendedArticlesSlice: T[];
    recommendedArtclesTotalCount: number;
}

export interface ArticleLink {
    name: string;
    href: string;
}

export interface ListItem {
    id: string;
    name: string;
    url: string;
    icon?: React.ReactElement;
}

export enum CampaignType {
    Email = 'Email',
    Link = 'Link',
    Twitter = 'Twitter',
    LinkedIn = 'LinkedIn',
    Facebook = 'Facebook',
    Qr = 'QrCode',
}

export enum CampaignTypeCode {
    Email = 'e',
    Link = 'l',
    Twitter = 't',
    LinkedIn = 'li',
    Facebook = 'fb',
    Qr = 'qr',
}

export interface Subscription {
    entityId: string;
    entityType: string;
    isEmail: boolean;
    entity: CategoryItem;
}

export interface CategorySubscription {
    entityId: string;
    entityType: string;
    isEmail: boolean;
    category: CategoryItem;
}

export interface ToolbarMenuItem {
    title: string;
    value: string;
    indentation?: number;
}

export interface BrowseArticleStat {
    tags: IdAndName[];
    desks: IdAndName[];
    trendingArticles: IdAndTitle[];
}

export interface ChannelArticleStats {
    trendingArticles: IdAndTitle[];
}

export interface ChannelStatsQuery {
    channelId: string;
    from?: Date;
    top?: number;
}

export interface Template {
    _id: string;
    author: string;
    title: string;
    html: string;
    publishedBy: Publisher;
    contributors: Partial<User>[];
    categories: IdAndName[];
    tags: IdAndName[];
    created: string;
    version: string;
    lastEdited: string;
    usedArticlesNumber: number;
    premiumChannel: string;
    channel: Channel;
}

export interface PromotionalContent {
    _id: string;
    name: string;
    link: string;
    description: string;
    categoriesCount: number;
    articlesCount: number;
    version: string;
    promotionPageId?: string;
    expiryDate?: Date;
}

export interface PromotionalContentCached extends PromotionalContent {
    sidebarImage: string;
    articleImage: string;
    newPageName?: string;
    __typename: string;
}

export type PromotionalContentNullable = PromotionalContentCached | null;

export interface PromotionPage {
    _id: string;
    name: string;
    html: string;
    teamsChannel: string;
    successTitle: string;
    successMessage: string;
    promotionalContent?: [PromotionalContent];
}

export interface PromotionalContentSummary {
    _id: string;
    name: string;
    link: string;
    sidebarImage: string;
    articleImage: string;
    expiryDate: Date;
    promotionPage?: PromotionPage;
}

export interface CurrentUserDetails {
    name: string;
    role: string;
    id?: string;
    isInternal: boolean;
    email?: string;
}

export interface ChannelOrderEntry {
    groupId: string;
    order: number;
}

export interface Channel {
    _id: string;
    name: string;
    channelOrder?: ChannelOrderEntry[];
    adminUsers: User[];
    url: string;
    createdBy: string;
    description: string;
    ownerType: ChannelOwner;
    ownerId: string;
    owner: IdAndName;
    price: string;
    profileImage?: string;
    coverImage?: string;
    channelActiveUsersCount: number;
    channelSubscribedUsers: User[];
    userChannelSubscription: ChannelSubscription;
    categories: CategoryItem[];
    isCurrentUserSubscribed: boolean;
    defaultTrialDurationDays?: number;
    type: ChannelTypes;
    articleCount?: number;
    state: ChannelState;
    hideDisclaimer: boolean;
    createdAt: string;
}

export interface ChannelGroup {
    _id: string;
    version?: number;
    name: string;
    description?: string;
    weight: number;
    channelIds?: string[];
    channelsCount: number;
    createdAt: string;
}

export enum ChannelState {
    Active = 'Active',
    Archived = 'Archived',
}

export enum ChannelOwner {
    desk = 'desk',
    contributor = 'contributor',
}

export enum ChannelType {
    premium = 'premium',
    free = 'free',
}

export enum ChannelFreeType {
    open = 'open',
    closed = 'closed',
}

export enum ChannelTypes {
    premium = 'premium',
    freeOpen = 'freeOpen',
    freeClosed = 'freeClosed',
}

export interface EmailNotificationSettings {
    channelNotifications?: boolean;
}

export enum ChannelSubscriptionType {
    Stripe = 'stripe',
    Account = 'account',
    External = 'external',
    NonCharge = 'noncharge',
    Trial = 'trial',
    Free = 'free',
}

export enum ChannelSubscriptionTypeByPayment {
    Paid = 'paid',
    Free = 'free',
    FreeTrial = 'freeTrial',
}

export type ChannelSubscriptionState =
    | 'active'
    | 'pending'
    | 'canceled'
    | 'expired'
    | 'paymentFailed'
    | 'rejected';

export interface InvoicePaymentRequestInput {
    channelId: string;
    legalEntityName: string;
    billingEmailAddress: string;
}

export interface ChannelSubscription {
    channel: Channel;
    channelId: string;
    subscribed: boolean;
    emailNotifications: boolean;
    subscriptionState: ChannelSubscriptionState;
    subscriptionType: ChannelSubscriptionType;
    rebate: boolean;
    createdAt: Date;
    updatedAt: Date;
    subscriptionId: string;
    cancelAtPeriodEnd?: boolean;
    cancelAt?: Date;
    cancelRequestedAt?: Date;
    acknowledgedNonChargeSubExpired?: boolean;
    pricePerMonth?: number;
    notes?: string;
    isFullArticleContent: boolean;
    accountNumber?: string;
    billingMethod?: string;
    trialDuration?: number;
}

export interface ChannelSubscriptionWithPaymentInfo extends ChannelSubscription {
    nextPayment: Date;
    paymentMethod: {
        type: string;
        details: string;
    };
}
